import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { PlugZap, ShieldAlert, History, Send, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import QboSandboxMapDialog from './QboSandboxMapDialog';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function QboLiveInvoicePanel({ jobId, onClose }) {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [note, setNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [showSandboxMap, setShowSandboxMap] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/api/qbo-live/status`, { headers: authHeaders() }),
        axios.get(`${API}/api/sales-invoicing/jobs/${jobId}/qbo-live/preview`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setPreview(p.data);
    } catch (err) {
      toast.error(getApiError(err, 'Could not load the QBO Live preview.'));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const doCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/api/sales-invoicing/jobs/${jobId}/qbo-live/create`, {}, { headers: authHeaders() });
      toast.success('Invoice created in QBO — read back and recorded.');
      await load();
    } catch (err) {
      toast.error(getApiError(err, 'Could not create this invoice in QBO.'));
    } finally {
      setCreating(false);
    }
  };

  const doConfirmDeletion = async (attemptId) => {
    if (!note.trim()) return;
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/api/sales-invoicing/jobs/${jobId}/qbo-live/attempts/${attemptId}/confirm-deletion`, { note }, { headers: authHeaders() });
      toast.success('Deletion confirmed — a retry is now possible.');
      setConfirmingId(null);
      setNote('');
      await load();
    } catch (err) {
      toast.error(getApiError(err, 'Could not record that confirmation.'));
    } finally {
      setSubmittingNote(false);
    }
  };

  const liveNameHint = (listType, value) => {
    if (!value) return null;
    const liveName = preview?.live_names?.[`${listType}:${value}`];
    return liveName ? <span className="text-emerald-700"> — live in Sandbox as "{liveName}"</span> : null;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="qbo-live-dialog">
        <DialogHeader>
          <DialogTitle>Create in QBO (Sandbox) — {preview?.hotel_name || 'Loading…'}</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500" data-testid="qbo-live-loading">Loading…</div>}

        {!loading && status && preview && (
          <div className="space-y-4" data-testid="qbo-live-content">
            <div className="rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs px-3 py-2" data-testid="qbo-live-scope-notice">
              A direct QBO Accounting API connection, separate from the Spreadsheet Sync staging export above.
              Nothing here is ever emailed — every invoice is created with EmailStatus "NotSet", and the result is always read back from QBO to confirm it, never assumed.
            </div>

            {!status.configured && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900" data-testid="qbo-live-not-configured">
                <div className="font-semibold text-sm flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" />QBO Sandbox isn't set up yet</div>
                <div className="text-xs mt-1">Go to Settings → QBO Sandbox connection to configure and connect it — nothing here can create an invoice until that's done.</div>
              </div>
            )}

            {status.configured && !status.connected && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900" data-testid="qbo-live-configured-not-connected">
                Configured, but not yet connected to a QBO Sandbox company — connect it in Settings → QBO Sandbox connection first.
              </div>
            )}

            {status.connected && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 flex items-center justify-between flex-wrap gap-2 text-sm text-emerald-800" data-testid="qbo-live-connected-badge">
                <span className="flex items-center gap-1.5"><PlugZap className="h-4 w-4" />Connected — {status.environment} company {status.realm_id}</span>
                <Button size="sm" variant="outline" onClick={() => setShowSandboxMap(true)} data-testid="qbo-live-open-sandbox-map-btn">
                  <MapPin className="h-3.5 w-3.5 mr-1" />Map to Sandbox references
                </Button>
              </div>
            )}

            <Separator />

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-2">Final review — exactly what Create would send</div>
              <div className="space-y-1.5 text-sm border rounded-md p-3" data-testid="qbo-live-payload-preview">
                <div data-testid="qbo-live-review-date">
                  <span className="text-slate-500">Date: </span>
                  {preview.scheduled_date ? new Date(preview.scheduled_date).toLocaleDateString('en-GB') : '—'}
                </div>
                <div data-testid="qbo-live-review-customer">
                  <span className="text-slate-500">Customer: </span>
                  {preview.payload.CustomerRef.name || '— unresolved —'}
                  {preview.payload.CustomerRef.value && <span className="text-slate-400"> (QBO ID {preview.payload.CustomerRef.value})</span>}
                  {liveNameHint('customer', preview.payload.CustomerRef.value)}
                </div>
                <div data-testid="qbo-live-review-location">
                  <span className="text-slate-500">Location: </span>
                  {preview.payload.DepartmentRef?.name || '— unresolved —'}
                  {preview.payload.DepartmentRef?.value && <span className="text-slate-400"> (QBO ID {preview.payload.DepartmentRef.value})</span>}
                  {liveNameHint('location', preview.payload.DepartmentRef?.value)}
                </div>
                <div data-testid="qbo-live-review-vat-code">
                  <span className="text-slate-500">VAT code: </span>
                  {preview.reviewed_vat_code ? (
                    <>
                      {preview.reviewed_vat_code.live_name || `QBO ID ${preview.reviewed_vat_code.qbo_id}`}
                      {preview.reviewed_vat_code.live_name && <span className="text-slate-400"> (QBO ID {preview.reviewed_vat_code.qbo_id})</span>}
                    </>
                  ) : '— not yet mapped for this Sandbox —'}
                </div>
                {preview.payload.Line.map((l, i) => {
                  const item = l.SalesItemLineDetail.ItemRef;
                  const cls = l.SalesItemLineDetail.ClassRef;
                  return (
                    <div key={i} className="pl-2 border-l-2 border-slate-200" data-testid={`qbo-live-line-${i}`}>
                      {item.name || '— unresolved item —'}
                      {item.value && <span className="text-slate-400"> (ID {item.value})</span>}
                      {liveNameHint('item', item.value)}
                      {' / '}
                      {cls?.name || '— unresolved class —'}
                      {cls?.value && <span className="text-slate-400"> (ID {cls.value})</span>}
                      {liveNameHint('class', cls?.value)}
                      {' — £'}{l.Amount.toFixed(2)}
                    </div>
                  );
                })}
                <Separator className="my-1" />
                <div className="flex justify-between text-xs text-slate-600 font-medium" data-testid="qbo-live-review-totals">
                  <span>Net £{(preview.net ?? 0).toFixed(2)}</span>
                  <span>VAT £{(preview.vat ?? 0).toFixed(2)}</span>
                  <span>Total £{(preview.total_inc_vat ?? 0).toFixed(2)}</span>
                </div>
                <div className="text-xs text-slate-400 pt-1">EmailStatus: NotSet — never sent to the customer</div>
              </div>
            </div>

            {preview.blocking_decisions.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 text-sm" data-testid="qbo-live-blocking">
                <div className="font-semibold text-xs uppercase mb-1">Not ready to send</div>
                {preview.blocking_decisions.map((b, i) => (
                  <div key={i} data-testid={`qbo-live-blocking-${i}`}>
                    {b.label} — {b.reason.replace(/_/g, ' ')}
                    {b.live_entity_name ? ` (currently live in Sandbox as "${b.live_entity_name}")` : ''}
                  </div>
                ))}
                {preview.blocking_decisions.some((b) => b.kind === 'not_found_in_connected_realm' || b.kind === 'not_verified_for_connected_realm') && (
                  <Button size="sm" variant="outline" className="mt-2 bg-white" onClick={() => setShowSandboxMap(true)} data-testid="qbo-live-blocking-open-sandbox-map-btn">
                    <MapPin className="h-3.5 w-3.5 mr-1" />Map to Sandbox references
                  </Button>
                )}
              </div>
            )}

            {preview.blocked_by_existing_attempt && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-900 text-sm space-y-2" data-testid="qbo-live-duplicate-block">
                <div className="font-semibold text-xs uppercase">An invoice already exists for this job</div>
                <div>QBO #{preview.blocked_by_existing_attempt.qbo_doc_number}, created {new Date(preview.blocked_by_existing_attempt.created_at).toLocaleString('en-GB')}.</div>
                <div>Confirm its deletion in QBO yourself, then record exactly how below — only then can a retry create a new one.</div>
                {confirmingId === preview.blocked_by_existing_attempt.attempt_id ? (
                  <div className="space-y-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="How did you confirm it was deleted in QBO?" data-testid="qbo-live-deletion-note-input" />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={!note.trim() || submittingNote} onClick={() => doConfirmDeletion(preview.blocked_by_existing_attempt.attempt_id)} data-testid="qbo-live-confirm-deletion-submit-btn">Confirm deletion</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setConfirmingId(null); setNote(''); }} data-testid="qbo-live-confirm-deletion-cancel-btn">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setConfirmingId(preview.blocked_by_existing_attempt.attempt_id)} data-testid="qbo-live-confirm-deletion-open-btn">I've confirmed it's deleted in QBO</Button>
                )}
              </div>
            )}

            {preview.can_create && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="w-full" disabled={creating} data-testid="qbo-live-create-btn"><Send className="h-4 w-4 mr-1.5" />Create in QBO</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Create this invoice in the QBO Sandbox now?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This sends a real request to the connected QuickBooks Sandbox company and creates an unsent invoice (EmailStatus NotSet). It will not email the customer. This cannot be undone from here — you would need to delete it in QBO yourself and confirm that here before retrying.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="qbo-live-create-confirm-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={doCreate} data-testid="qbo-live-create-confirm-btn">Create in QBO</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {preview.attempts.length > 0 && (
              <div>
                <div className="text-xs font-medium text-slate-400 uppercase mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" />Attempt history — never deleted</div>
                <div className="space-y-1" data-testid="qbo-live-attempts-list">
                  {preview.attempts.map((a) => (
                    <div key={a.id} className="text-xs border rounded-md p-2 flex items-center justify-between" data-testid={`qbo-live-attempt-${a.id}`}>
                      <span>QBO #{a.qbo_doc_number} — {new Date(a.created_at).toLocaleString('en-GB')}</span>
                      <Badge variant={a.status === 'created' ? 'default' : a.status === 'deletion_confirmed' ? 'secondary' : 'destructive'}>{a.status.replace(/_/g, ' ')}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {showSandboxMap && (
        <QboSandboxMapDialog jobId={jobId} onClose={() => setShowSandboxMap(false)} onApplied={load} />
      )}
    </Dialog>
  );
}
